import './RouletteNewLot.sass';

import React, { FC, useCallback, useMemo, useRef, useState } from 'react';
import { Form, Input, FormItem, ScrollView, Button, BottomBar } from '$uikit';
import { ATTACHMENTS_COUNT_LIMIT, classNames } from '$utils';
import { ColorsEnum } from '$shared/constants/colors';
import { IcCloud28, IcGallery28, IcVideo28 } from '$assets';
import { useTranslator } from '$hooks';
import AttachPickerManager from '$shared/components/AttachPickerManager/AttachPickerManager';
import { AttachPickerManagerRef } from '$shared/components/AttachPickerManager/AttachPickerManager.interface';
import { RouletteNewLotProps } from '$core/NewRoulette/RouletteNewLot/RouletteNewLot.interface';
import { toastActions } from '$store/toast';
import { useDispatch, useSelector } from 'react-redux';
import { RootState } from '$store/rootReducer';
import { store } from '$store/store';
import { attachItemsSelector, attachPhotosSelector } from '$store/attach';
import { AttachBodyItem } from '$store/attach/interface';
import { cloudActions } from '$store/cloud';
import { goTo } from '$navigation/helper';
import router from '$navigation/router';
import { CLOUD } from '$shared/constants/pages';
import {
  rouletteActions,
  rouletteEditLotSelector,
  rouletteSelector,
} from '$store/roulette';

export const RouletteNewLot: FC<RouletteNewLotProps> = () => {
  const dispatch = useDispatch();
  const t = useTranslator();
  const attachManager = useRef<AttachPickerManagerRef | null>(null);
  const [photoInputKey, setPhotoInputKey] = useState(0);
  const [videoInputKey, setVideoInputKey] = useState(0);
  const { lots } = useSelector(rouletteSelector)!;
  const { title, color, description, id } = useSelector(rouletteEditLotSelector)!;
  const pickerManagerId = `roulette_lott_${id}`;
  const mVerified = useSelector((state: RootState) => state.main.mVerified);

  const attachMedia = useSelector((state: RootState) =>
    attachItemsSelector(state, pickerManagerId),
  );

  const handlePhotoChange = useCallback(
    async ({ target: { files } }: React.ChangeEvent<HTMLInputElement>) => {
      if (!files) return;
      attachManager.current?.uploadImages(files);
      setPhotoInputKey(Date.now());
    },
    [attachManager, setPhotoInputKey],
  );

  const handleVideoChange = useCallback(
    ({ target }) => {
      if (!target.files.length) {
        return;
      }

      attachManager.current?.uploadVideo(target.files[0]);
      setVideoInputKey(Date.now());
    },
    [attachManager],
  );

  const handleAttachFromCloud = useCallback(() => {
    const count = attachPhotosSelector(store.getState(), pickerManagerId).length;
    if (count >= ATTACHMENTS_COUNT_LIMIT) {
      dispatch(
        toastActions.setToastFail(
          t('photos_limit_error_custom', { limit: ATTACHMENTS_COUNT_LIMIT }),
        ),
      );
      return;
    }

    const route = router.getState();
    dispatch(
      cloudActions.setAttachParams({
        pickerId: pickerManagerId,
        routeName: route.name,
        routeParams: route.params,
      }),
    );
    goTo(CLOUD, {}, { limit: ATTACHMENTS_COUNT_LIMIT - count });
  }, [dispatch, pickerManagerId, t]);

  const nameError = title.length > 24;

  const handleLottCreate = useCallback(() => {
    if (!title.length) {
      dispatch(toastActions.setToastFail(t('roulette_lot_error_empty_title')));
    } else if (nameError) {
      dispatch(
        toastActions.setToastFail(t('roulette_lot_error_title_length', { length: 24 })),
      );
    } else if (!attachMedia.length) {
      dispatch(toastActions.setToastFail(t('roulette_lot_error_empty_attach')));
    } else if (attachMedia === 'photo_uploading') {
      dispatch(toastActions.setToastFail(t('photo_uploading_err')));
    } else {
      dispatch(
        rouletteActions.saveLot({
          id,
          title,
          color,
          description,
          attachments: attachMedia as AttachBodyItem[],
        }),
      );
    }
  }, [title, color, description, attachMedia]);

  const isEdit = useMemo(() => {
    return lots.find((lot) => lot.id === id);
  }, [id, lots]);

  return (
    <>
      <ScrollView>
        <Form>
          <FormItem
            title={t('roulette_lot_title')}
            attention={nameError}
            description={
              nameError
                ? t('roulette_lot_title_max_length', {
                    length: 24,
                  })
                : t('roulette_lot_title_desc')
            }
          >
            <Input
              value={title}
              onChange={(e) => dispatch(rouletteActions.setEditLotTitle(e.target.value))}
            />
          </FormItem>
          <FormItem title={t('roulette_lot_color')}>
            <div className="RouletteNewLot__colors__wrap">
              <div className="RouletteNewLot__colors">
                {Object.values(ColorsEnum).map((item) => (
                  <div
                    key={item}
                    className={classNames('RouletteNewLot__colors__item', item, {
                      active: item === color,
                    })}
                    onClick={(e) => dispatch(rouletteActions.setEditLotColor(item))}
                  />
                ))}
              </div>
            </div>
          </FormItem>
          <FormItem title={t('roulette_lot_description')}>
            <Input
              multiline
              value={description}
              onChange={(e) =>
                dispatch(rouletteActions.setEditLotDescription(e.target.value))
              }
              placeholder={t('roulette_lot_description_placeholder')}
            />
            <AttachPickerManager ref={attachManager} pickerId={pickerManagerId} />
            <div className="RouletteNewLot__attach_button_wrap">
              {mVerified ? (
                <div className="RouletteNewLot__attach_button">
                  <Button
                    size="small"
                    mode="secondary"
                    icon={<IcCloud28 />}
                    onClick={handleAttachFromCloud}
                  >
                    {t('new_post_attach_cloud')}
                  </Button>
                </div>
              ) : null}
              <div className="RouletteNewLot__attach_button">
                <Button size="small" mode="secondary" icon={<IcGallery28 />}>
                  {t('new_post_attach_photo')}
                </Button>
                <input
                  key={photoInputKey}
                  type="file"
                  accept="image/*"
                  onChange={handlePhotoChange}
                  multiple
                />
              </div>
              <div className="RouletteNewLot__attach_button">
                <Button mode="secondary" icon={<IcVideo28 />} size="small">
                  {t('new_post_attach_video')}
                </Button>
                <input
                  key={videoInputKey}
                  type="file"
                  onChange={handleVideoChange}
                  accept="video/mp4,video/quicktime"
                />
              </div>
            </div>
            <div className="RouletteNewLot__lotCaption">{t('roulette_lot_caption')}</div>
          </FormItem>
        </Form>
      </ScrollView>
      <BottomBar>
        <Button stretched onClick={handleLottCreate}>
          {isEdit ? t('roulette_lot_edit_lot_button') : t('roulette_lot_add_lot_button')}
        </Button>
      </BottomBar>
    </>
  );
};
